import {
  EyeIcon,
  ArrowDownTrayIcon,
  EnvelopeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Sidebar from '../common/Sidebar';

function MemberMagazine() {
  const navigate = useNavigate();
  const [printRequests, setPrintRequests] = useState(new Set());
  const [showRequestConfirm, setShowRequestConfirm] = useState(null);

  // Mock data for quarterly magazines, starting from current year going back 2 years
  const magazines = [
    {
      id: 1,
      title: 'Winter 2024',
      quarter: 'Q1 2024',
      publishedAt: '2024-01-01',
      url: '#',
      pdfSize: '12.3 MB',
      pages: 48,
    },
    {
      id: 2,
      title: 'Autumn 2023',
      quarter: 'Q4 2023',
      publishedAt: '2023-10-01',
      url: '#',
      pdfSize: '10.8 MB',
      pages: 44,
    },
    {
      id: 3,
      title: 'Summer 2023',
      quarter: 'Q3 2023',
      publishedAt: '2023-07-01',
      url: '#',
      pdfSize: '11.5 MB',
      pages: 42,
    },
    {
      id: 4,
      title: 'Spring 2023',
      quarter: 'Q2 2023',
      publishedAt: '2023-04-01',
      url: '#',
      pdfSize: '9.7 MB',
      pages: 40,
    },
    {
      id: 5,
      title: 'Winter 2023',
      quarter: 'Q1 2023',
      publishedAt: '2023-01-01',
      url: '#',
      pdfSize: '10.2 MB',
      pages: 38,
    },
    {
      id: 6,
      title: 'Autumn 2022',
      quarter: 'Q4 2022',
      publishedAt: '2022-10-01',
      url: '#',
      pdfSize: '11.1 MB',
      pages: 46,
    },
    {
      id: 7,
      title: 'Summer 2022',
      quarter: 'Q3 2022',
      publishedAt: '2022-07-01',
      url: '#',
      pdfSize: '10.5 MB',
      pages: 42,
    },
    {
      id: 8,
      title: 'Spring 2022',
      quarter: 'Q2 2022',
      publishedAt: '2022-04-01',
      url: '#',
      pdfSize: '9.9 MB',
      pages: 40,
    },
  ];

  const handleView = magazineId => {
    navigate(`/magazine/${magazineId}`);
  };

  const handleDownload = magazine => {
    // In a real app, this would trigger a PDF download
    alert(`Downloading ${magazine.title} (${magazine.pdfSize})`);
  };

  const handleRequestPrint = magazine => {
    setShowRequestConfirm(magazine.id);
    setTimeout(() => {
      setPrintRequests(prev => new Set([...prev, magazine.id]));
      setShowRequestConfirm(null);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-hdlcc-black">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64">
          <div className="p-6">
            <div className="bg-hdlcc-darkGray text-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold">Members Magazine</h2>
                  <p className="text-gray-400">
                    View, download, or request printed copies of our quarterly club magazine
                  </p>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-900/20 border border-blue-600/50 p-4 rounded-lg mb-6">
                <p className="text-sm text-gray-300">
                  <strong className="text-blue-400">Magazine Access:</strong> All members can view
                  and download digital copies. Request a printed copy to be posted to your address
                  using the button below. You can manage your magazine preferences in your{' '}
                  <a href="/profile" className="text-hdlcc-lightBlue hover:underline">
                    profile settings
                  </a>
                  .
                </p>
              </div>

              <div className="bg-hdlcc-gray rounded shadow">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-hdlcc-darkGray">
                        <th className="px-4 py-3 text-left">Issue</th>
                        <th className="px-4 py-3 text-left">Published</th>
                        <th className="px-4 py-3 text-left">Details</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {magazines.map(magazine => (
                        <tr
                          key={magazine.id}
                          className="border-b border-hdlcc-darkGray/50 hover:bg-hdlcc-darkGray"
                        >
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-semibold">{magazine.title}</div>
                              <div className="text-sm text-gray-400">{magazine.quarter}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {new Date(magazine.publishedAt).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400">
                            {magazine.pages} pages • {magazine.pdfSize}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleView(magazine.id)}
                                className="flex items-center gap-1 bg-hdlcc-lightBlue text-white px-3 py-2 rounded hover:bg-hdlcc-blue text-sm"
                                title="View online"
                              >
                                <EyeIcon className="w-4 h-4" />
                                View
                              </button>
                              <button
                                onClick={() => handleDownload(magazine)}
                                className="flex items-center gap-1 bg-green-700 text-white px-3 py-2 rounded hover:bg-green-600 text-sm"
                                title="Download PDF"
                              >
                                <ArrowDownTrayIcon className="w-4 h-4" />
                                Download
                              </button>
                              {printRequests.has(magazine.id) ? (
                                <button
                                  disabled
                                  className="flex items-center gap-1 bg-green-900 text-green-300 px-3 py-2 rounded text-sm cursor-not-allowed"
                                  title="Print copy requested"
                                >
                                  <CheckCircleIcon className="w-4 h-4" />
                                  Requested
                                </button>
                              ) : showRequestConfirm === magazine.id ? (
                                <button
                                  disabled
                                  className="flex items-center gap-1 bg-gray-700 text-gray-300 px-3 py-2 rounded text-sm"
                                >
                                  Processing...
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleRequestPrint(magazine)}
                                  className="flex items-center gap-1 bg-gray-700 text-white px-3 py-2 rounded hover:bg-gray-600 text-sm"
                                  title="Request printed copy by post"
                                >
                                  <EnvelopeIcon className="w-4 h-4" />
                                  Print Copy
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Print Request Info */}
              {printRequests.size > 0 && (
                <div className="mt-6 bg-green-900/20 border border-green-600/50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-green-400 mr-2 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-300">
                        <strong className="text-green-400">Print Request Submitted:</strong> You
                        have requested {printRequests.size} printed{' '}
                        {printRequests.size === 1 ? 'copy' : 'copies'}. These will be posted to your
                        registered address within 5-7 working days.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MemberMagazine;
